import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bike,
  ClipboardList,
  Grid3x3,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  ShoppingCart,
  Store,
  User,
  Warehouse,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { PromoBanner } from "@/components/PromoBanner";
import { DealBanner } from "@/components/DealBanner";

export const EsLogo = ({ className = "h-10 w-10" }) => (
  <svg viewBox="0 0 48 48" className={className} role="img" aria-label="EMPIRE-SHOP" data-testid="es-logo">
    <defs>
      <linearGradient id="es-shield" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0D5C3A" />
        <stop offset="1" stopColor="#042F1D" />
      </linearGradient>
    </defs>
    <path
      d="M24 2 L43 8 V24 C43 36 34 43 24 46 C14 43 5 36 5 24 V8 Z"
      fill="url(#es-shield)"
      stroke="#D97706"
      strokeWidth="2.4"
      strokeLinejoin="round"
    />
    <text x="12.5" y="30.5" fontFamily="'Plus Jakarta Sans', sans-serif" fontSize="18" fontWeight="800" fill="#D97706">E</text>
    <text x="21.5" y="34" fontFamily="'Plus Jakarta Sans', sans-serif" fontSize="22" fontWeight="800" fill="#FFFFFF">S</text>
  </svg>
);

export const Brand = ({ compact = false, dark = false }) => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
    <EsLogo className="h-10 w-10 drop-shadow-[0_6px_16px_rgba(6,78,59,0.35)]" />
    {!compact && (
      <span className="leading-none">
        <span className={`block font-display text-base font-extrabold tracking-tight sm:text-lg ${dark ? "text-white" : "text-slate-900"}`}>
          EMPIRE<span className="text-gold">-SHOP</span>
        </span>
        <span className={`mt-0.5 block text-[10px] font-semibold tracking-[0.12em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
          GUINÉE · ENTREPÔT
        </span>
      </span>
    )}
  </Link>
);

export const Header = () => {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("search", query.trim());
    if (cat) params.set("category", cat);
    navigate(`/catalogue?${params.toString()}`);
  };

  return (
    <header className="sticky top-0 z-50 shadow-[0_1px_0_rgba(15,23,42,0.06)]">
      <div className="border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          <Brand />

          <form onSubmit={submit} className="ml-auto hidden flex-1 max-w-2xl md:block">
            <div className="flex overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:border-emerald-700 focus-within:ring-4 focus-within:ring-emerald-700/10">
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                className="w-40 shrink-0 border-r border-slate-200 bg-slate-50 px-2 text-xs font-semibold text-slate-600 outline-none"
                data-testid="header-category-select"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un produit, une marque, un SKU…"
                className="min-h-11 flex-1 px-3 text-sm outline-none placeholder:text-slate-400"
                data-testid="header-search-input"
              />
              <button type="submit" className="grid w-12 place-items-center bg-gold text-white transition-colors hover:bg-gold-dark" data-testid="header-search-button">
                <Search className="h-5 w-5" />
              </button>
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1.5 md:ml-0">
          <Link
            to="/professionnels"
            className="hidden items-center gap-1.5 rounded-xl border border-brand/30 bg-brand-light px-3 py-2 text-xs font-semibold text-brand transition-colors hover:bg-brand hover:text-white md:inline-flex"
            data-testid="header-pro-link"
          >
            <Store className="h-4 w-4" /> Vendre avec nous
          </Link>
          <NotificationBell />
          <Link
            to="/panier"
            className="relative grid h-10 w-10 place-items-center rounded-xl border border-slate-200 text-slate-700 transition-colors hover:bg-slate-50"
            data-testid="header-cart-button"
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span
                className="font-num absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-gold px-1 text-[10px] font-bold text-white"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 text-slate-700 transition-colors hover:bg-slate-50"
              data-testid="header-account-button"
            >
              {menuOpen ? <X className="h-5 w-5" /> : user ? <User className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            {menuOpen && (
              <div className="ds-card absolute right-0 top-12 z-50 w-60 overflow-hidden p-1" data-testid="account-menu">
                {user ? (
                  <>
                    <div className="border-b border-slate-100 px-3 py-2">
                      <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                      <p className="truncate text-xs text-slate-500">{user.email}</p>
                      <span className="ds-chip mt-1 border-brand/30 bg-brand-light text-brand">{user.role}</span>
                    </div>
                    <MenuLink to="/compte" icon={User} label="Mon compte" testid="menu-account" />
                    <MenuLink to="/commandes" icon={ClipboardList} label="Mes commandes" testid="menu-orders" />
                    {user.role === "admin" && (
                      <MenuLink to="/admin" icon={LayoutDashboard} label="Espace administrateur" testid="menu-admin" />
                    )}
                    {user.role === "warehouse" && (
                      <MenuLink to="/admin/stock" icon={Warehouse} label="Espace entrepôt" testid="menu-warehouse" />
                    )}
                    {user.role === "vendor" ? (
                      <MenuLink to="/vendeur" icon={Store} label="Espace vendeur" testid="menu-vendor" />
                    ) : (
                      user.role === "client" && (
                        <MenuLink to="/devenir-vendeur" icon={Store} label="Devenir vendeur" testid="menu-become-vendor" />
                      )
                    )}
                    <MenuLink to="/professionnels" icon={Store} label="Espace professionnel" testid="menu-pro-space" />
                    {user.role === "courier" && (
                      <MenuLink to="/livreur" icon={Bike} label="Mes livraisons" testid="menu-courier" />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        await logout();
                        navigate("/");
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                      data-testid="menu-logout"
                    >
                      <LogOut className="h-4 w-4" /> Se déconnecter
                    </button>
                  </>
                ) : (
                  <>
                    <MenuLink to="/connexion" icon={User} label="Se connecter" testid="menu-login" />
                    <MenuLink to="/inscription" icon={User} label="Créer un compte" testid="menu-register" />
                    <MenuLink to="/professionnels" icon={Store} label="Vendre avec nous" testid="menu-pro-space-guest" />
                  </>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white px-4 pb-3 md:hidden">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un produit…"
            className="ds-input pl-9"
            data-testid="header-search-input-mobile"
          />
        </div>
      </form>

      <nav className="bg-brand text-white" data-testid="department-nav">
        <div className="no-scrollbar mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 sm:px-6">
          <Link
            to="/catalogue"
            className="flex shrink-0 items-center gap-1.5 px-2.5 py-2.5 text-xs font-semibold text-white/90 transition-colors hover:text-gold"
            data-testid="dept-all"
          >
            <Grid3x3 className="h-4 w-4" /> Tous les rayons
          </Link>
          {categories.slice(0, 8).map((c) => (
            <Link
              key={c.id}
              to={`/catalogue?category=${c.id}`}
              className="shrink-0 whitespace-nowrap px-2.5 py-2.5 text-xs font-medium text-white/80 transition-colors hover:text-gold"
              data-testid={`dept-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
          <Link
            to="/offres"
            className="shrink-0 whitespace-nowrap px-2.5 py-2.5 text-xs font-bold text-gold"
            data-testid="dept-promos"
          >
            Offres du jour
          </Link>
          <Link
            to="/catalogue?wholesale=1"
            className="shrink-0 whitespace-nowrap px-2.5 py-2.5 text-xs font-semibold text-white/80 transition-colors hover:text-gold"
            data-testid="dept-wholesale"
          >
            Prix de gros
          </Link>
        </div>
      </nav>
    </header>
  );
};

const MenuLink = ({ to, icon: Icon, label, testid }) => (
  <Link
    to={to}
    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
    data-testid={testid}
  >
    <Icon className="h-4 w-4" /> {label}
  </Link>
);

export const BottomNav = () => {
  const { count } = useCart();
  const items = [
    { to: "/", icon: Home, label: "Accueil" },
    { to: "/catalogue", icon: Grid3x3, label: "Catalogue" },
    { to: "/panier", icon: ShoppingCart, label: "Panier", badge: count },
    { to: "/commandes", icon: ClipboardList, label: "Commandes" },
    { to: "/compte", icon: User, label: "Compte" },
  ];
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden"
      data-testid="bottom-nav"
    >
      <div className="grid grid-cols-5">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                isActive ? "text-brand" : "text-slate-500"
              }`
            }
            data-testid={`bottomnav-${it.label.toLowerCase()}`}
          >
            <it.icon className="h-5 w-5" />
            {it.label}
            {it.badge > 0 && (
              <span className="font-num absolute right-4 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-gold px-1 text-[9px] font-bold text-white">
                {it.badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export const Footer = () => (
  <footer className="mt-16 border-t border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-3 sm:px-6">
      <div>
        <Brand />
        <p className="mt-3 max-w-xs text-sm text-slate-500">
          Entrepôt physique de 300 m² en Guinée (adresse exacte communiquée prochainement). Vente au détail et en gros, livraison depuis l'entrepôt pour 10 000 GNF.
        </p>
      </div>
      <div className="text-sm">
        <p className="ds-label">Navigation</p>
        <ul className="mt-2 space-y-1 text-slate-600">
          <li><Link to="/catalogue" className="hover:text-brand">Catalogue</Link></li>
          <li><Link to="/professionnels" className="hover:text-brand" data-testid="footer-pro-link">Vendre avec nous</Link></li>
          <li><Link to="/commandes" className="hover:text-brand">Mes commandes</Link></li>
          <li><Link to="/connexion" className="hover:text-brand">Connexion</Link></li>
        </ul>
      </div>
      <div className="text-sm">
        <p className="ds-label">Paiement avant livraison</p>
        <ul className="mt-2 space-y-1 text-slate-600">
          <li>Orange Money</li>
          <li>MTN Mobile Money</li>
          <li>Virement / dépôt bancaire</li>
        </ul>
        <p className="mt-2 text-xs text-slate-400">
          Le règlement se fait au moment de la commande ; la préparation démarre après confirmation de l'encaissement.
        </p>
        <p className="mt-3 text-xs text-slate-400">© {new Date().getFullYear()} EMPIRE-SHOP Guinée</p>
      </div>
    </div>
  </footer>
);

export const ShopLayout = ({ children }) => (
  <div className="min-h-screen bg-background pb-16 md:pb-0">
    <DealBanner />
    <PromoBanner />
    <Header />
    <main>{children}</main>
    <Footer />
    <BottomNav />
  </div>
);
